import { useEffect, useState } from 'react';
import { Avatar } from '@/components/apps/Avatar';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import type { AppMember, AppRecord } from '@/lib/app-records';
import {
  type AiSegment, type AppReview, type ReviewCriterion, averageScore, matchAiSegment, saveAppReview, SCORE_LABELS, SCORE_MAX,
} from '@/lib/app-reviews';

interface AppRecordReviewProps {
  record: AppRecord;
  grid: ReviewCriterion[];
  reviews: AppReview[];
  members: AppMember[];
  userId: string | null;
  onSaved: () => void;
}

const formatAi = (segment: AiSegment | undefined) =>
  segment ? `${segment.value.toLocaleString('fr-FR')}${segment.max ? ` / ${segment.max}` : ''}` : '—';

/** My review of an object with the app's grid, next to the AI's grid of the run; the others' reviews below. */
export const AppRecordReview = ({ record, grid, reviews, members, userId, onSaved }: AppRecordReviewProps) => {
  const mine = reviews.find((review) => review.reviewerUserId === userId) ?? null;
  const [scores, setScores] = useState<Record<string, number>>(mine?.scores ?? {});
  const [comment, setComment] = useState(mine?.comment ?? '');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const nameOf = (id: string) => members.find((member) => member.userId === id)?.name ?? 'Quelqu’un';

  const ai = record.aiGrid ?? [];
  useEffect(() => { setScores(mine?.scores ?? {}); setComment(mine?.comment ?? ''); }, [record.id, mine?.id, mine?.updatedAt]);
  useEffect(() => { setMessage(null); }, [record.id]);

  const complete = grid.every((criterion) => scores[criterion.key] !== undefined);
  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await saveAppReview(record.id, scores, comment);
      setMessage('Revue enregistrée.');
      onSaved();
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : 'La revue n’a pas pu être enregistrée.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto rounded-xl border border-domino-cardBorder">
        <table className="min-w-full text-sm">
          <thead className="bg-domino-muted text-left text-xs font-medium text-domino-inkSoft">
            <tr><th className="px-3 py-2">Critère</th><th className="px-3 py-2">IA</th><th className="px-3 py-2">Ma note</th></tr>
          </thead>
          <tbody>
            {grid.map((criterion) => {
              const aiSegment = matchAiSegment(criterion, ai);
              const value = scores[criterion.key];
              return (
                <tr key={criterion.key} className="border-t border-domino-cardBorder">
                  <td className="px-3 py-2 font-medium text-domino-ink">{criterion.label}</td>
                  <td className="whitespace-nowrap px-3 py-2 tabular-nums text-domino-inkSoft">{formatAi(aiSegment)}</td>
                  <td className="px-3 py-2">
                    <div role="radiogroup" aria-label={`Note : ${criterion.label}`} className="inline-flex overflow-hidden rounded-lg border border-domino-cardBorder">
                      {SCORE_LABELS.map((label, score) => (
                        <button key={label} type="button" role="radio" aria-checked={value === score} title={`${score} — ${label}`}
                          onClick={() => setScores({ ...scores, [criterion.key]: score })}
                          className={cn('border-l border-domino-cardBorder px-2 py-1 text-xs first:border-l-0',
                            value === score ? 'bg-domino-aubergine text-white' : 'bg-domino-white text-domino-ink hover:bg-domino-muted')}>
                          {label}
                        </button>
                      ))}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <textarea value={comment} onChange={(event) => setComment(event.target.value)} rows={2} maxLength={4000}
        placeholder="Ce qui est bien fait, ce qui manque, la prochaine étape…" aria-label="Commentaire de revue"
        className="w-full rounded-xl border border-domino-cardBorder p-2 text-sm text-domino-ink placeholder:text-domino-inkSoft focus:border-domino-aubergine focus:outline-none" />
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={() => void save()} disabled={saving || !complete}>{saving ? 'Enregistrement…' : mine ? 'Mettre à jour ma revue' : 'Enregistrer ma revue'}</Button>
        {!complete && <span className="text-xs text-domino-inkSoft">Notez chaque critère.</span>}
        {message && <span role="status" className="text-xs text-domino-inkSoft">{message}</span>}
      </div>
      {reviews.filter((review) => review.reviewerUserId !== userId).map((review) => (
        <div key={review.id} className="flex gap-2 rounded-xl bg-domino-warm px-3 py-2">
          <Avatar name={nameOf(review.reviewerUserId)} />
          <div className="min-w-0 text-sm">
            <p className="text-xs text-domino-inkSoft">
              <span className="font-medium text-domino-ink">{nameOf(review.reviewerUserId)}</span>
              {' · '}moyenne {averageScore(review, grid).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} / {SCORE_MAX}
            </p>
            {review.comment && <p className="mt-0.5 text-domino-ink">{review.comment}</p>}
          </div>
        </div>
      ))}
    </div>
  );
};
